import { useFormik } from "formik";
import React, { useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { FormElements } from "../../../shared/ui/forms/FormElements";
import HeadingH4 from "../../../shared/ui/headings/headingH4/HeadingH4";
import Icon from "../../../shared/ui/icon/Icon";
import {
    formStructureEditPatientRegistryRow,
    formStructureEditPatientRegistryRowDomicile,
    formStructureEditPatientRegistryRowResidence
} from "./patientRegistryData";

import SecondaryButton from "../../../shared/ui/buttons/secondary-button/SecondaryButton";
import {
    createPatient,
    updatePatient
} from "../../../../redux/order/orderCrud";
import { useDispatch, useSelector } from "react-redux";
import { selectSelectedCustomer } from "../../../../redux/customer/customerSlice";
import * as routeName from "../../../routes/routeNameAuthType";
import { GetPatientAction } from "../../../../redux/patient/patientActions";
import { useRoute } from "../../../utils/useRoute";
import { setErrorTypeAction } from "../../../../redux/errors/errorsActions";
import { getValidationsPatient } from "./data/validations";
import BtnPrimary from "../../../../shared/bootstrap/button/primary/BtnPrimary";
import BtnSecondary from "../../../../shared/bootstrap/button/secondary/BtnSecondary";
import { useLanguageForShared } from "../../../../shared/utils/languages/useLanguageForShared";
import {
    getCustomerDistrictOptions,
    hasCustomerDistricts,
    isDistrictGuidAllowed,
    isCustomerDistrictRequired,
    sanitizePatientDistrictPayload
} from "../../../utils/districtUtils";

const ADDRESS_FIELDS = ["street", "number", "internal", "cap", "city", "province"];
const hasAddressValue = (address) =>
    ADDRESS_FIELDS.some((key) => {
        const value = address?.[key];
        return value !== undefined && value !== null && `${value}`.trim() !== "";
    });
const normalizeAddressValue = (value) =>
    value === undefined || value === null ? "" : `${value}`.trim();

// Helper to check if any residence/domicile field differs
function isDomicileDifferent(residence, domicile) {
    if (!residence || !domicile) return false;
    if (!hasAddressValue(domicile)) return false;

    return ADDRESS_FIELDS.some(
        key => normalizeAddressValue(residence[key]) !== normalizeAddressValue(domicile[key])
    );
}

const getPatientFormValue = (patient, isDifferent) => ({
    ...(patient || {}),
    domicile: isDifferent ? patient?.domicile : undefined,
});

const normalizeComparableValue = (value) => {
    if (value === undefined || value === null || value === "") {
        return undefined;
    }

    if (Array.isArray(value)) {
        const normalizedArray = value
            .map(normalizeComparableValue)
            .filter((ele) => ele !== undefined);

        return normalizedArray.length ? normalizedArray : undefined;
    }

    if (typeof value === "object") {
        const normalizedObject = Object.keys(value)
            .sort()
            .reduce((acc, key) => {
                if (key === "isDomicileDifferent") {
                    return acc;
                }

                const normalizedValue = normalizeComparableValue(value[key]);
                if (normalizedValue !== undefined) {
                    acc[key] = normalizedValue;
                }
                return acc;
            }, {});

        return Object.keys(normalizedObject).length ? normalizedObject : undefined;
    }

    if (typeof value === "string") {
        return value.trim();
    }

    return value;
};

const getComparablePatientHash = (customer, patientValue) => {
    const isDifferent = patientValue?.isDomicileDifferent ?? isDomicileDifferent(patientValue?.residence, patientValue?.domicile);
    const normalizedPayload = sanitizePatientDistrictPayload(customer, getPatientFormValue(patientValue, isDifferent));
    return JSON.stringify(normalizeComparableValue(normalizedPayload) || {});
};

const PatientRegistry = ({ patient = {}, closeModalHandler }) => {
    const { t } = useLanguageForShared();

    const customer = useSelector(selectSelectedCustomer);

    const dispatch = useDispatch();

    const { changeRoute } = useRoute();
    const districtOptions = getCustomerDistrictOptions(customer);
    const validationsPatient = useMemo(() => getValidationsPatient(customer), [customer]);

    // Compute initial value for the checkbox
    const initialIsDomicileDifferent = isDomicileDifferent(
        patient.residence,
        patient.domicile
    );

    const formik = useFormik({
        initialValues: {
            ...getPatientFormValue(patient, initialIsDomicileDifferent),
            isDomicileDifferent: (process.env.REACT_APP_ENABLE_DOMICILE_TOGGLE !== "false") ? initialIsDomicileDifferent : true,
        },
        validationSchema: validationsPatient,
        onSubmit: (values) => {
            const payload = (process.env.REACT_APP_ENABLE_DISTRICT !== "false") ? sanitizePatientDistrictPayload(customer, values) : values;

            if (process.env.REACT_APP_ENABLE_DISTRICT !== "false") {
                const districtGuid = payload?.residence?.districtGuid;
                if (isCustomerDistrictRequired(customer) && !districtGuid) {
                    dispatch(setErrorTypeAction("Il distretto di residenza è obbligatorio"));
                    return;
                }

                if (hasCustomerDistricts(customer) && !isDistrictGuidAllowed(customer, districtGuid)) {
                    dispatch(setErrorTypeAction("Il distretto selezionato non appartiene al cliente"));
                    return;
                }
            }

            if (patient?.id) {
                updatePatient(payload)
                    .then((res) => {
                        //const callback = () => changeRoute(routeName.selectedPatientPage);
                        dispatch(GetPatientAction(t, res.id, /*callback*/));
                    })
                    .catch((err) => {
                        const message = err?.response?.data?.Message || err?.response?.data?.message;
                        if (message === "ERR_CF_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfAlreadyRegistered')))
                        } else if (message === "ERR_CF_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfWrong')))
                        } else if (message === "ERR_EMAIL_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailAlreadyRegistered')))
                        } else if (message === "ERR_EMAIL_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailWrong')))
                        } else if (message === "ERR_DISTRICT_CUSTOMER_REQUIRED") {
                            dispatch(setErrorTypeAction("Se selezioni un distretto devi indicare il cliente"))
                        } else if (message === "ERR_DISTRICT_CUSTOMER_INVALID") {
                            dispatch(setErrorTypeAction("Il distretto selezionato non appartiene al cliente"))
                        } else {
                            dispatch(
                                dispatch(setErrorTypeAction(t('sg.msg.errorUpdatePatient')))
                            );
                        }
                    });
            } else {
                createPatient(payload, customer.id)
                    .then((res) => {
                        const callback = () => changeRoute(routeName.products);
                        dispatch(GetPatientAction(t, res.id, callback));
                    })
                    .catch((err) => {
                        const message = err?.response?.data?.Message || err?.response?.data?.message;
                        if (message === "ERR_CF_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfAlreadyRegistered')))
                        } else if (message === "ERR_CF_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.cfWrong')))
                        } else if (message === "ERR_EMAIL_DUPLICATE") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailAlreadyRegistered')))
                        } else if (message === "ERR_EMAIL_INCOHERENT") {
                            dispatch(setErrorTypeAction(t('sg.msg.emailWrong')))
                        } else if (message === "ERR_DISTRICT_CUSTOMER_REQUIRED") {
                            dispatch(setErrorTypeAction("Se selezioni un distretto devi indicare il cliente"))
                        } else if (message === "ERR_DISTRICT_CUSTOMER_INVALID") {
                            dispatch(setErrorTypeAction("Il distretto selezionato non appartiene al cliente"))
                        } else {
                            dispatch(
                                setErrorTypeAction(t('sg.msg.errorInsertPatient'))
                            );
                        }
                    });
            }
            closeModalHandler();
        }
    });

    const originalPatientHash = useMemo(
        () => getComparablePatientHash(customer, {
            ...getPatientFormValue(patient, initialIsDomicileDifferent),
            isDomicileDifferent: initialIsDomicileDifferent,
        }),
        [customer, patient, initialIsDomicileDifferent]
    );

    const currentPatientHash = getComparablePatientHash(customer, formik.values);
    const hasPatientChanges = !!patient?.id && originalPatientHash !== currentPatientHash;

    const resetDomicileValidationState = React.useCallback(() => {
        formik.setTouched((prevTouched) => ({
            ...prevTouched,
            domicile: undefined,
        }), false);
        formik.setErrors((prevErrors) => ({
            ...prevErrors,
            domicile: undefined,
        }));
    }, [formik]);
    return (
        <div>
            <div className="d-flex">
                <p
                    className="sub-title mb-4">{patient?.id ? t('sg.editPatientRegistry') : t('sg.addPatientRegistry')}</p>
            </div>
            <Form onSubmit={formik.handleSubmit}>
                <FormElements
                    structure={formStructureEditPatientRegistryRow(t)}
                    formik={formik}
                ></FormElements>

                {(process.env.REACT_APP_SHOW_PATIENT_GENDER === "true") && (
                    <FormElements
                        structure={[
                            {
                                name: "gender",
                                label: t('sg.patient.gender'),
                                type: "select",
                                options: [
                                    { label: t(`common.unknown`), value: "" },
                                    { label: t(`common.male`), value: "Male" },
                                    { label: t(`common.female`), value: "Female" }
                                ],
                                col: { xs: "12", sm: "6", lg: "3" }
                            }
                        ]}
                        formik={formik}
                    ></FormElements>
                )}

                <FormElements
                    structure={formStructureEditPatientRegistryRowResidence(t, districtOptions)}
                    formik={formik}
                ></FormElements>

                {/* Checkbox between residence and domicile (solo se enableDomicileToggle, default Linde) */}
                {(process.env.REACT_APP_ENABLE_DOMICILE_TOGGLE !== "false") && (
                <div className="mb-3" id="isDomicileDifferent">
                    <Form.Check
                        type="checkbox"
                        label={t('sg.patient.differentDomicile')}
                        name="isDomicileDifferent"
                        checked={formik.values.isDomicileDifferent}
                        onChange={e => {
                            const checked = e.target.checked;
                            formik.setFieldValue("isDomicileDifferent", checked, false);
                            if (!checked) {
                                formik.setFieldValue("domicile", undefined, false);
                                resetDomicileValidationState();
                            }
                        }}
                    />
                </div>
                )}

                <FormElements
                    structure={formStructureEditPatientRegistryRowDomicile(t)}
                    formik={formik}
                    disabled={(process.env.REACT_APP_ENABLE_DOMICILE_TOGGLE !== "false") ? !formik.values.isDomicileDifferent : false}
                />

                {hasPatientChanges && (
                    <div className="alert alert-warning mt-3 mb-0" role="alert">
                        {t("sg.updateRegistryProviderWarning")}
                    </div>
                )}

                <Row className="justify-content-end mt-5">
                    <Col xs="auto">
                        <BtnSecondary
                            onClick={() => closeModalHandler()/*changeRoute(routeName.selectedPatientPage)*/}
                        >
                            {t('action.cancel')}
                        </BtnSecondary>
                    </Col>
                    <Col xs="auto">
                        <BtnPrimary
                            type={"submit"}
                            data-test={
                                patient?.id ? "edit-patient-test" : "create-patient-test"
                            }
                        >
                            {patient?.id ? t('action.edit')
                                : t('sg.form.action.addNewService')}
                        </BtnPrimary>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default PatientRegistry;
